'use client'

import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react'

type ToastType = 'success' | 'error' | 'info'
interface Toast {
  id: string
  type: ToastType
  message: string
  description?: string
}

interface ToastContextValue {
  show: (type: ToastType, message: string, description?: string) => void
  success: (message: string, description?: string) => void
  error: (message: string, description?: string) => void
  info: (message: string, description?: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const show = useCallback((type: ToastType, message: string, description?: string) => {
    const id = Math.random().toString(36).slice(2, 9)
    setToasts(prev => [...prev, { id, type, message, description }])
    // Auto-dismiss через 4 секунды
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, 4000)
  }, [])

  const success = useCallback((message: string, description?: string) => show('success', message, description), [show])
  const error = useCallback((message: string, description?: string) => show('error', message, description), [show])
  const info = useCallback((message: string, description?: string) => show('info', message, description), [show])

  function dismiss(id: string) {
    setToasts(prev => prev.filter(t => t.id !== id))
  }

  return (
    <ToastContext.Provider value={{ show, success, error, info }}>
      {children}
      {/* Контейнер toast'ов */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm pointer-events-none">
        {toasts.map(t => (
          <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const t = useTranslations()
  const Icon = toast.type === 'success' ? CheckCircle2
              : toast.type === 'error' ? AlertCircle
              : Info
  // Premium стиль — белый фон + цветная иконка + цветная узкая полоска слева
  const accentColor = toast.type === 'success' ? 'bg-status-green-text'
                    : toast.type === 'error'   ? 'bg-red-500'
                    : 'bg-blue-500'
  const iconColor = toast.type === 'success' ? 'text-status-green-text'
                 : toast.type === 'error'   ? 'text-red-600'
                 : 'text-blue-600'

  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10)
    return () => clearTimeout(t)
  }, [])

  return (
    <div
      className={
        'pointer-events-auto relative rounded-lg border-hairline border-ink-200 bg-white shadow-sm ' +
        'pl-4 pr-2 py-2.5 flex items-start gap-2.5 min-w-[280px] overflow-hidden ' +
        'transition-all duration-200 ' +
        (visible ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0')
      }
    >
      {/* Цветная полоска слева — типа Linear */}
      <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${accentColor}`} />

      <Icon size={15} className={`shrink-0 mt-0.5 ${iconColor}`} strokeWidth={2} />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-ink-900">{toast.message}</div>
        {toast.description && (
          <div className="text-xs text-ink-500 mt-0.5">{toast.description}</div>
        )}
      </div>
      <button
        onClick={onDismiss}
        className="shrink-0 -mr-1 -mt-1 p-1 rounded text-ink-400 hover:text-ink-900 hover:bg-ink-100 transition-colors"
        aria-label={t('common.close')}
      >
        <X size={13} />
      </button>
    </div>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Fallback на консоль если провайдер не подключен (safety)
    return {
      show: (t: ToastType, m: string) => console.log(`[toast:${t}]`, m),
      success: (m: string) => console.log('[toast:success]', m),
      error: (m: string) => console.error('[toast:error]', m),
      info: (m: string) => console.info('[toast:info]', m),
    } as ToastContextValue
  }
  return ctx
}
